import {Box, Flex, showMessage, Text, Title} from 'exsportia-components'
import {DateTime} from 'luxon'
import {FC, useEffect, useRef} from 'react'
import {useNavigate, useParams} from 'react-router-dom'

import {canSaveClaim, claimCardState} from '../../api/promo-codes/card-state'
import {useClaim, useClaimPromo} from '../../api/promo-codes/my-promos'
import {ClaimState} from '../../api/promo-codes/types'
import {GridLayout} from '../../layout/grid-layout/grid-layout'
import {useAuthState} from '../../services/auth/hooks'
import {theme} from '../../theme/theme'
import {Button} from '../../ui/components/button/button'
import {useLocale} from '../../utils/i18n'
import {PromoCard} from './promo-card'

// `/p/:token` has no venue in it, but the app lives under `/:venueId`; look the link up and move on.
export const ClaimRedirect: FC = () => {
  const {l} = useLocale()
  const {token} = useParams()
  const navigate = useNavigate()
  const {data, isError} = useClaim(token)
  useEffect(() => {
    if (data?.promo.venue.id) {
      navigate(`/${data.promo.venue.id}/p/${token}`, {replace: true})
    }
  }, [data, token, navigate])
  return (
    <Box p='24px'>
      <Text
        text={isError ? l('promo_claim_not_found') : l('loading')}
        settings={{color: theme.colors['text-secondary']}}
      />
    </Box>
  )
}

const NOTICE: Partial<Record<ClaimState, 'promo_claim_ended' | 'promo_claim_gone' | 'promo_claim_have' | 'promo_claim_paused' | 'promo_claim_soon'>> = {
  ALREADY_HAVE: 'promo_claim_have',
  ALL_GONE: 'promo_claim_gone',
  ENDED: 'promo_claim_ended',
  PAUSED: 'promo_claim_paused',
  NOT_STARTED: 'promo_claim_soon',
}

const startDay = (validFrom?: string | null) => {
  const date = validFrom ? DateTime.fromISO(validFrom) : null
  return date?.isValid ? date.toFormat('d LLL') : null
}

// What a shared link or QR opens: the card and a way to save it to the account (sign in first).
export const ClaimPage: FC = () => {
  const {l} = useLocale()
  const {venueId, token} = useParams()
  const navigate = useNavigate()
  const {isLoggedIn, signIn} = useAuthState(venueId)
  const {data, isLoading, isError} = useClaim(token)
  const {mutate: claim, isLoading: isSaving, isSuccess, isError: isSaveError, data: saved} = useClaimPromo()
  const wantsSave = useRef(false)

  // Signed in after tapping "Sign in to save it": save once on arrival.
  useEffect(() => {
    if (wantsSave.current && isLoggedIn && token && data && canSaveClaim(data.state)) {
      wantsSave.current = false
      claim(token)
    }
  }, [isLoggedIn, token, data?.state, claim])

  if (isLoading) {
    return (
      <GridLayout>
        <Text text={l('loading')} settings={{color: theme.colors['text-secondary']}} />
      </GridLayout>
    )
  }
  if (isError || !data) {
    return (
      <GridLayout>
        <Text text={l('promo_claim_not_found')} />
      </GridLayout>
    )
  }

  const {promo, state} = data
  const batch = (promo.mode ?? '').toUpperCase() === 'BATCH'
  const notice = isSuccess ? null : NOTICE[state]
  const canSave = canSaveClaim(state) && !isSuccess
  const starts = state === 'NOT_STARTED' ? startDay(promo.validFrom) : null
  // Founder call (16 Oct): the chip shows whenever by-token sends a code (a shared promotion for
  // anyone, or the person's own copy). Batch, sent-only and new-only codes come without one.
  const shownCode = saved?.code ?? promo.code ?? null
  const cardState = claimCardState(state, Boolean(notice))
  const canCopy = Boolean(shownCode) && (cardState === 'ready' || cardState === 'soon')
  const copyCode = () => {
    if (!shownCode) {
      return
    }
    navigator.clipboard.writeText(shownCode.replace(/\s+/g, ''))
      .then(() => showMessage(l('copied'), 'success'))
      .catch(() => undefined)
  }
  const onSave = () => {
    if (!isLoggedIn) {
      wantsSave.current = true
      signIn()
      return
    }
    if (token) {
      claim(token)
    }
  }

  return (
    <GridLayout>
      <Box width='100%'>
        <Box py='8px'>
          <Title
            styleType='h1'
            text={l(batch ? 'promo_claim_head_batch' : 'promo_claim_head', {venue: promo.venue.name})}
          />
        </Box>
        <PromoCard
          card={{
            ...promo,
            code: shownCode,
            expiryText: starts ? l('promo_card_starts', {date: starts}) : promo.expiryText,
          }}
          state={cardState}
        />
        {canCopy && (
          <Box pt='8px'>
            <Button type='secondary' onClick={copyCode}>
              {l('promo_copy_code')}
            </Button>
          </Box>
        )}
        {promo.fine && (
          <Box pt='8px'>
            <Text text={promo.fine} settings={{color: theme.colors['text-secondary'], fontSize: '13px'}} />
          </Box>
        )}
        {notice && (
          <Box py='12px'>
            <Text text={starts ? l('promo_claim_soon_save', {date: starts}) : l(notice, {venue: promo.venue.name})} />
          </Box>
        )}
        {isSuccess && (
          <Box py='12px'>
            <Text text={l('promo_claim_saved')} settings={{color: theme.colors.success}} />
          </Box>
        )}
        {isSaveError && (
          <Box py='12px'>
            <Text text={l('promo_claim_failed')} settings={{color: theme.colors.error}} />
          </Box>
        )}
        <Flex
          gap='8px'
          py='12px'
          flexDirection='column'
        >
          {canSave && (
            <Button
              type='primary'
              disabled={isSaving}
              onClick={onSave}
            >
              {isSaving ? l('promo_claim_saving') : l(isLoggedIn ? 'promo_claim_save' : 'promo_claim_sign_in')}
            </Button>
          )}
          {(isSuccess || state === 'ALREADY_HAVE') && (
            <Button
              type='secondary'
              onClick={() => navigate(`/${venueId}/vouchers`)}
            >
              {l('promo_claim_see_mine')}
            </Button>
          )}
          <Button
            type='secondary'
            onClick={() => navigate(`/${venueId}`)}
          >
            {l('promo_claim_book_now')}
          </Button>
        </Flex>
      </Box>
    </GridLayout>
  )
}
